import React, { FC, lazy, Suspense } from 'react';
import PrivateRoute from 'components/common/PrivateRoute';
import { Redirect, Route, Switch } from 'react-router-dom';
import FetchingData from 'components/common/Spinner/FetchingData';
import routes from 'config/routes';
import { Roles } from 'config/general';
import RedirectTo from 'pages/Integrations/Locks/components/Redirect';

const UserFeed = lazy(() => import('pages/User/UserFeed'));
const UserProfile = lazy(() => import('pages/User/Profile'));
const UserDoorAccess = lazy(() => import('pages/User/DoorAccess'));
const UserCreds = lazy(() => import('pages/User/Credentials'));
const VisitorPass = lazy(() => import('pages/User/VisitorPins'));
const Subscriptions = lazy(() => import('pages/User/Subscriptions'));
const UserAmenities = lazy(() => import('pages/UserAmenities'));
const ActivityFeed = lazy(() => import('pages/ActivityFeed'));
const Users = lazy(() => import('pages/Users'));
const UserGroups = lazy(() => import('pages/Groups'));
const SitesAndAccessPoints = lazy(() => import('pages/SitesAndAccessPoints'));
const Schedules = lazy(() => import('pages/Schedules'));
const HealthCheck = lazy(() => import('pages/HealthCheck'));
const VisitorSettings = lazy(() => import('pages/Auth/VisitorSettings'));
const AuditLogs = lazy(() => import('pages/AuditLogs'));
const LoginPage = lazy(() => import('pages/Auth/Login'));
const EnterWorkspacePage = lazy(() => import('pages/Auth/EnterWorkspace'));
const JoinWorkspace = lazy(() => import('pages/Auth/JoinWorkspace'));
const EmailVerification = lazy(() => import('pages/Auth/EmailVerification'));
const ConfirmEmail = lazy(() => import('pages/Auth/ConfirmEmail'));
const NewPassword = lazy(() => import('pages/Auth/NewPassword'));
const ErrorPage = lazy(() => import('pages/ErrorPage'));
const Settings = lazy(() => import('pages/Settings'));
const InstallerAccess = lazy(() => import('pages/InstallerAccess'));
const Integrations = lazy(() => import('pages/Integrations'));
const Reports = lazy(() => import('pages/Reports'));
const Hardware = lazy(() => import('pages/Hardware'));
const VisitorAccess = lazy(() => import('pages/IntercomSettings'));
const VisitorPins = lazy(() => import('pages/VisitorPins'));
const Announcements = lazy(() => import('pages/Announcements'));
const Amenities = lazy(() => import('pages/Amenities'));
const AmenityPayments = lazy(() => import('pages/AmenityPayments'));
const PackageRoom = lazy(() => import('pages/PackageRoom'));

// const TailgatingDetection = lazy(() => import('legacy/components/tailgating-detection'));
// const VisitorAccess2 = lazy(() => import('legacy/components/visitorAccess'));
// const VisitorHealthCheckQuestions = lazy(() => import('legacy/components/visitorAccess/questions'));

const CommandCenter = lazy(() => import('pages/CommandCenter'));

const RentGpt = lazy(() => import('pages/RentGpt'));
const Billing = lazy(() => import('pages/Billing'));
const HelpSupport = lazy(() => import('pages/HelpSupport'));
const SwiftCall = lazy(() => import('pages/SwiftCall'));
const Alerts = lazy(() => import('pages/Alerts'));

const Routes: FC = () => {
    return (
        <Suspense fallback={<FetchingData loading />}>
            <Switch>
                <PrivateRoute
                    allowedRoles={[Roles.admin, Roles.user]}
                    exact
                    path={[routes.home, routes.activityFeed.root]}
                    component={ActivityFeed}
                />
                <PrivateRoute path={routes.sites.root} component={SitesAndAccessPoints} />
                <PrivateRoute path={routes.settings.root} component={Settings} />
                <PrivateRoute path={routes.installerAccess.root} component={InstallerAccess} />
                <PrivateRoute path={routes.users.root} component={Users} />
                <PrivateRoute path={routes.intercomSettings.root} component={VisitorAccess} />
                <PrivateRoute path={routes.visitorPins.root} component={VisitorPins} />
                <PrivateRoute path={routes.schedules.root} component={Schedules} />
                <PrivateRoute path={routes.integrations.root} component={Integrations} />
                <PrivateRoute exact path={routes.auditLogs.root} component={AuditLogs} />
                <PrivateRoute path={routes.hardware.root} component={Hardware} />

                <PrivateRoute path={routes.user.feed} component={UserFeed} allowedRoles={[Roles.user]} />
                <PrivateRoute path={routes.user.profile.root} component={UserProfile} allowedRoles={[Roles.user]} />
                <PrivateRoute path={routes.user.access} component={UserDoorAccess} allowedRoles={[Roles.user]} exact />
                <PrivateRoute path={routes.user.credentials} component={UserCreds} allowedRoles={[Roles.user]} exact />
                <PrivateRoute path={routes.user.passes} component={VisitorPass} allowedRoles={[Roles.user]} />
                <PrivateRoute
                    path={[routes.user.amenities, routes.user.amenityBookings]}
                    component={UserAmenities}
                    allowedRoles={[Roles.user]}
                />
                <Route path={routes.user.subscriptions} component={Subscriptions} />
                {/* Provide Access to app when user is navigated from mobile app */}

                <PrivateRoute path={routes.userGroups.root} component={UserGroups} />
                <PrivateRoute path={routes.reports.root} component={Reports} />
                <PrivateRoute path={routes.auth.visitorSettings} component={VisitorSettings} />
                <PrivateRoute path={routes.healthCheck.root} component={HealthCheck} />
                <Route path={routes.auth.enterWorkspace} component={EnterWorkspacePage} />
                <Route path={routes.auth.login} component={LoginPage} />
                <Route path={routes.auth.confirmEmail} component={ConfirmEmail} />
                <Route path={routes.auth.forgotPassword} component={NewPassword} />
                <Route path={routes.auth.joinWorkspace} component={JoinWorkspace} />
                <Route path={routes.auth.emailVerification} component={EmailVerification} />
                {/* <Route path={routes.healthCheck.test} component={VisitorHealthCheckQuestions} /> */}
                {/* <Route path={routes.intercomSettings.access} component={VisitorAccess2} /> */}
                {/* <PrivateRoute exact path='/tailgating-detection' component={TailgatingDetection} /> */}
                <PrivateRoute path={routes.augustRedirect.root} component={RedirectTo} />
                <PrivateRoute path={routes.announcements.root} allowedRoles={[Roles.admin, Roles.user]} component={Announcements} />
                <PrivateRoute path={routes.amenities.root} component={Amenities} />
                <PrivateRoute path={routes.amenityPayments.root} component={AmenityPayments} />

                {/* Stripe Express onboarding currently returns the admin's browser to
                    `/cc/amenity-payments/…` — the backend's own default
                    (swiftpassweb settings.py:569-572), written when no FE page existed. That is a
                    Command Center path, and Command Center redirects non-super-admins straight out,
                    so a building admin — who IS authorized for every Connect endpoint — would land
                    on a dead end at the end of onboarding. These two forwards make the flow
                    complete today; the settings should be repointed at the real routes, after which
                    they can be deleted. `search` is preserved because Stripe appends `?site_id=`.
                    Registered BEFORE the Command Center route below, which would otherwise match
                    them first. */}
                <Route
                    exact
                    path={routes.amenityPayments.legacyCcConnectReturn}
                    render={({ location }) => <Redirect to={{ pathname: routes.amenityPayments.connectReturn, search: location.search }} />}
                />
                <Route
                    exact
                    path={routes.amenityPayments.legacyCcConnectRefresh}
                    render={({ location }) => (
                        <Redirect to={{ pathname: routes.amenityPayments.connectRefresh, search: location.search }} />
                    )}
                />

                <PrivateRoute path={routes.commandCenter.root} component={CommandCenter} />

                <PrivateRoute path={routes.rentGpt.root} component={RentGpt} />
                <PrivateRoute path={routes.billing.root} component={Billing} />
                <PrivateRoute path={routes.helpSupport.root} component={HelpSupport} />
                <Route
                    exact
                    path={routes.packageDelivery.root}
                    render={({ location }) => <Redirect to={{ pathname: routes.packageRoom.delivery, search: location.search }} />}
                />
                <PrivateRoute path={routes.packageRoom.root} component={PackageRoom} />

                <PrivateRoute path={routes.swiftCall.root} component={SwiftCall} />

                <PrivateRoute path={routes.alerts.root} component={Alerts} />

                <Route component={ErrorPage} />
            </Switch>
        </Suspense>
    );
};

export default Routes;
